import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Intercom, {
  shutdown as shutdownIntercom,
} from '@intercom/messenger-js-sdk'
import { useAuthStore } from '../stores/authStore.js'
import { useTenantStore } from '../stores/tenantStore.js'
import {
  MARKETS_GC_TIME,
  MARKETS_QUERY_KEY,
  MARKETS_STALE_TIME,
} from './useMarkets.js'
import { FEATURES, FREIGHT_TYPE_TO_AUDIENCE } from '../lib/config/constants.js'
import { posthogIdentify } from '../lib/logging/posthog.js'
import { teardownLocalSession } from '../features/auth/lib/authSession.js'
import { redirectToExpectedTenant } from '../features/auth/lib/tenantRedirect.js'
import { api } from '../lib/api.js'
import { KindeTokenAdapter } from '../features/auth/lib/KindeTokenAdapter.js'

/**
 * converts specialty freight to intercom audience
 * @param {string|null} specialtyFreight freight type from kinde token
 * @returns {string} audience name or empty string
 */
export const getAudienceFromSpecialtyFreight = (specialtyFreight) => {
  if (!specialtyFreight) {
    return ''
  }
  const audience = FREIGHT_TYPE_TO_AUDIENCE[specialtyFreight.toLowerCase()]
  return audience || ''
}

/**
 * useAuthedBootstrap
 *
 * Runs once when the authenticated branch (ProtectedLayout) mounts. The
 * branch can only mount after AuthGate has settled the auth decision, so
 * there is no auth-state guard here — and no /callback guard: the callback
 * page lives in the public branch, so the MV-3293 class of bug (bootstrap
 * fetches aborted by the post-login redirect, read as a dead session) is
 * structurally impossible.
 *
 * Fetches tenant config and user into Zustand, warms the markets query,
 * identifies the user in PostHog, and manages the Intercom lifecycle. On
 * unmount (logout / session teardown) it resets tenant state — authedLoaded
 * must not carry into the next session, or route guards would briefly evaluate the
 * previous session's feature flags — and shuts Intercom down.
 */
export function useAuthedBootstrap() {
  const hasRun = useRef(false)
  const intercomBooted = useRef(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    async function bootstrap() {
      try {
        // Nothing in this app writes 'market-storage'. The removal only
        // evicts it from browsers that still hold a value for it.
        localStorage.removeItem('market-storage')

        // 1. Fetch tenant config
        try {
          await useTenantStore.getState().fetchConfig()
        } catch (configError) {
          console.error(
            'bootstrap: getConfig() failed, tearing down session:',
            configError.message,
          )
          // Local teardown only — a transient config failure at tab-open must
          // not kill the Kinde SSO session for every other tab
          teardownLocalSession('bootstrap-config-failed')
          return
        }

        // 2. Fetch user data — always extract uid from JWT (source of truth)
        const tokenData = KindeTokenAdapter.extractTokenData(
          JSON.parse(localStorage.getItem('kindeToken') || 'null'),
        )
        const authState = useAuthStore.getState()
        let uid = tokenData?.uid
        if (!uid) {
          // Fallback to persisted state only if token extraction fails
          uid = authState.user?.uid
        }
        let user
        try {
          user = await authState.fetchUser(uid)
        } catch (userError) {
          console.error(
            'bootstrap: getUser() failed, tearing down session:',
            userError.message,
          )
          teardownLocalSession('bootstrap-user-failed')
          return
        }

        // Restrict IMC users to their tenant's URL — second line of defense
        // behind the same check in KindeCallback
        if (user?.role === 'imc' && redirectToExpectedTenant(user?.tenant)) {
          return
        }

        // 3. Fetch markets into the query cache useMarkets() reads
        try {
          await queryClient.fetchQuery({
            queryKey: MARKETS_QUERY_KEY,
            queryFn: () => api.getMarketsList(),
            staleTime: MARKETS_STALE_TIME,
            gcTime: MARKETS_GC_TIME,
          })
        } catch (marketError) {
          console.error('bootstrap: listMarkets() failed:', marketError.message)
        }

        // 4. Identify user in logging services
        if (user) {
          posthogIdentify(user)
        }
      } catch (error) {
        console.error('bootstrap: Unexpected error:', error.message)
      }
    }

    bootstrap()
  }, [])

  // Leaving the authenticated branch = the session ended (logout, teardown).
  // Reset tenant state and Intercom so nothing leaks into the next session.
  useEffect(
    () => () => {
      useTenantStore.getState().reset()
      if (intercomBooted.current) {
        intercomBooted.current = false
        shutdownIntercom()
      }
    },
    [],
  )

  // Intercom lifecycle. Boots once user data and the authed-config response
  // are available and the flag is on; shuts down if the flag flips off.
  // Read user via subscription only for the boot condition — bootIntercom
  // reads the rest of its data itself.
  const user = useAuthStore((s) => s.user)
  const authedLoaded = useTenantStore((s) => s.authedLoaded)
  const intercomEnabled = useTenantStore((s) =>
    s.isFeatureEnabled(FEATURES.INTERCOM),
  )
  useEffect(() => {
    const shouldBoot = authedLoaded && intercomEnabled && !!user

    if (shouldBoot && !intercomBooted.current) {
      intercomBooted.current = true
      void bootIntercom(user)
      return
    }

    if (!shouldBoot && intercomBooted.current) {
      intercomBooted.current = false
      shutdownIntercom()
    }
  }, [authedLoaded, intercomEnabled, user])
}

async function bootIntercom(user) {
  const kindeToken = localStorage.getItem('kindeToken')
  const specialtyFreight = kindeToken
    ? KindeTokenAdapter.extractSpecialtyFreight(JSON.parse(kindeToken))
    : null
  const audience = getAudienceFromSpecialtyFreight(specialtyFreight)
  const baseSettings = {
    app_id: 'sd2wyn6o',
    user_id: user.uid,
    name: `${user.firstName} ${user.lastName}`.trim(),
    email: user.email,
    created_at: Date.now(),
    companies: [{ name: user?.company?.name, id: user?.company?.uid }],
    Audience: audience,
  }

  try {
    const intercomData = await api.getIntercomToken()
    Intercom({ ...baseSettings, intercom_user_jwt: intercomData?.token })
  } catch (intercomError) {
    console.log(
      'bootstrap: Intercom token failed, using fallback',
      intercomError,
    )
    Intercom(baseSettings)
  }
}
